import * as React from 'react'
import { GoogleBreadcrumbsListJson } from '@repo/analytics/google/breadcrumb-list-json'
import { useLocale } from 'next-intl'
import { defaultLanguage } from 'cf-i18n/constants'
import { rootPath } from '@repo/web-utils/root-path'
import { tw } from 'utils/classnames'

import { Link } from '../link'

export interface BreadcrumbItem {
  path?: string
  title: string
  className?: string
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  separator?: string | React.ReactNode
}

export function Breadcrumbs({
  items,
  separator = '/',
  ...props
}: React.ComponentProps<'nav'> & BreadcrumbsProps) {
  const locale = useLocale()

  const renderItem = (item: BreadcrumbItem) => {
    const { path, title, className = '' } = item

    return (
      <div className="flex items-center">
        {path ? (
          <Link
            key={title}
            href={path}
            className={tw('text-cf-blue-700 hover:text-cf-theme-green-700', className)}
          >
            {title}
          </Link>
        ) : (
          <span className={className} key={title}>
            {title}
          </span>
        )}
      </div>
    )
  }

  return (
    <>
      <GoogleBreadcrumbsListJson
        items={items.map((item, index) => ({
          position: index + 1,
          name: item.title,
          item: item.path
            ? `${rootPath}${locale === defaultLanguage ? '' : `/${locale}`}${item.path}`
            : undefined,
        }))}
      />
      <nav aria-label="breadcrumb" {...props}>
        <ol className="flex list-none flex-wrap items-center">
          {items.map((item, index) => (
            <React.Fragment key={`breadcrumbs_${item.title}`}>
              <li>{renderItem(item)}</li>
              {index !== items.length - 1 ? (
                <li className="flex">
                  <span className="mx-[5px]">{separator}</span>
                </li>
              ) : null}
            </React.Fragment>
          ))}
        </ol>
      </nav>
    </>
  )
}
